import type { Dictionary } from 'lodash';

import { QuizField } from '@/modules/corporate/modules/questions/fields/types';
import { Option, OptionResponse } from '@/types/schema';

import { OptionsListItemTypeEnum } from './types';

export const isValidSubmission: QuizField['isValidSubmission'] = (question, submission) =>
  Boolean(submission?.optionResponse?.length);

interface Params {
  option: Pick<Option, 'optionId' | 'optionRelativeScore'>;
  isQuestionSubmitted: boolean;
  selectedOptionsById: Dictionary<Pick<OptionResponse, 'optionId'>>;
  correctOptionsById: Dictionary<Pick<Option, 'optionId'>>;
  isWeightedOption?: boolean;
  options?: Pick<Option, 'optionRelativeScore'>[];
}

export const getOptionListItemType = (params: Params) => {
  const {
    option,
    isQuestionSubmitted,
    selectedOptionsById,
    correctOptionsById,
    isWeightedOption,
    options,
  } = params;
  const isAnswered = Boolean(selectedOptionsById[option.optionId]);
  const isCorrectOption = Boolean(correctOptionsById[option.optionId]);

  if (!isQuestionSubmitted) {
    return isAnswered ? OptionsListItemTypeEnum.Selected : OptionsListItemTypeEnum.Default;
  }

  // WeightedOption questions award partial credit per option, so correctness is derived
  // from the option's own relative score against the best-scoring option, rather than
  // the binary correctOptionsById list (which treats every non-zero-weight option alike).
  // TODO(tech-debt): the backend only exposes a per-question evaluationStatus, not a
  // per-option credit breakdown, so this comparison has to stay here. See
  // docs/weighted-option-quiz-tech-debt.md.
  if (isWeightedOption) {
    const score = option.optionRelativeScore ?? 0;
    const maxScore = Math.max(0, ...(options ?? []).map((o) => o.optionRelativeScore ?? 0));
    const isBestScore = maxScore > 0 && score === maxScore;

    if (isAnswered) {
      if (score <= 0) return OptionsListItemTypeEnum.Incorrect;
      return isBestScore
        ? OptionsListItemTypeEnum.Correct
        : OptionsListItemTypeEnum.PartiallyCorrect;
    }

    if (score <= 0) return OptionsListItemTypeEnum.Default;
    return isBestScore
      ? OptionsListItemTypeEnum.Missed
      : OptionsListItemTypeEnum.PartiallyCorrectMissed;
  }

  if (isAnswered) {
    return isCorrectOption ? OptionsListItemTypeEnum.Correct : OptionsListItemTypeEnum.Incorrect;
  }

  return isCorrectOption ? OptionsListItemTypeEnum.Missed : OptionsListItemTypeEnum.Default;
};
